import React, { useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import { AlertModal } from '../Modal';
import '../Filters/Filters.scss';
import { FaTrash } from 'react-icons/fa6';
import { getNamedQueries } from '../../services';
import { toast } from 'react-toastify';

const SavedSearchFilter = ({
  value,
  onChange,
  onDelete,
  onRefreshNeeded,
  setSavedQueriesCount,
  onApplySavedSearch, // New prop to apply saved search criteria
}) => {
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState(null);
  const [savedSearchOptions, setSavedSearchOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch saved searches from API
  const fetchSavedSearches = async () => {
    setIsLoading(true);
    try {
      const response = await getNamedQueries();
      setSavedQueriesCount(response?.length || 0);
      const options = [
        { value: 'new', label: 'New Search', isDeletable: false },
        ...(response || []).map(search => ({
          value: search.name,
          label: search.name,
          isDeletable: true,
          searchCriteria: search.searchCriteria,
          repositoryIds: search.repositoryIds,
          sorts: search?.sorts || [],
        }))
      ];
      setSavedSearchOptions(options);
    } catch (error) {
      toast.error('Failed to load saved searches. Please refresh the page.', {
        position: 'top-right',
        autoClose: 4000,
      });
      // Fallback to empty array with just "New Search"
      setSavedSearchOptions([{ value: 'new', label: 'New Search', isDeletable: false }]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedSearches();
  }, []);

  // Listen for refresh requests from parent
  useEffect(() => {
    if (onRefreshNeeded && typeof onRefreshNeeded === 'number') {
      fetchSavedSearches();
    }
  }, [onRefreshNeeded]);

  const handleDeleteConfirm = async () => {
    if (searchToDelete) {
      try {
        await onDelete && onDelete(searchToDelete.value);
        // Refresh the list after successful deletion
        await fetchSavedSearches();
        setShowDeleteAlert(false);
        setSearchToDelete(null);
      } catch (error) {
        console.error('Failed to delete saved search:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteAlert(false);
    setSearchToDelete(null);
  };

  // Custom option component with delete icon
  const Option = (props) => {
    const { data } = props;
    
    return (
      <components.Option {...props}>
        <div className="saved-search-select-option">
          <span>{data.label}</span>
          {data.isDeletable && (
            <button
              type="button"
              className="saved-search-delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setSearchToDelete(data);
                setShowDeleteAlert(true);
              }}
            >
              <FaTrash />
            </button>
          )}
        </div>
      </components.Option>
    );
  };
  const val = savedSearchOptions?.find(option => option.value === value) || null
  
  return (
    <>
      <div className="filter-control">
        <label className="filter-label">Saved Search</label>
        <Select
          className="saved-search-select"
          classNamePrefix="filter-select"
          options={savedSearchOptions?.filter((item)=> item?.value !== val?.value)}
          value={savedSearchOptions.find(option => option.value === value) || null}
          onChange={(selectedOption) => {
            const selectedData = savedSearchOptions.find(option => option.value === selectedOption.value);
            onChange && onChange(selectedOption.value, selectedData);
            
            // Apply saved search criteria immediately
            if (onApplySavedSearch) {
              if (selectedOption.value === 'new') {
                // For "New Search", keep all repos selected and show empty state
                onApplySavedSearch({
                  searchCriteria: { children: [] }
                  // Don't pass repositoryIds to avoid resetting repos
                });
              } else if (selectedData && selectedData.searchCriteria) {
                // Apply saved search criteria
                onApplySavedSearch({
                  searchCriteria: selectedData.searchCriteria,
                  repositoryIds: selectedData.repositoryIds,
                  sorts: selectedData?.sorts || []
                });
              }
            }
          }}
          isLoading={isLoading}
          isDisabled={isLoading}
          placeholder={isLoading ? "Loading saved searches..." : "Select saved search"}
          components={{
            Option,
          }}
          styles={{
            control: (base, state) => ({
              ...base,
              minHeight: '36px',
            }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isSelected ? '#fff9f2' : state.isFocused ? 'rgba(247, 146, 26, 0.1)' : '#ffffff',
            }),
            singleValue: (base) => ({
              ...base,
            }),
            menu: (base) => ({
              ...base,
              borderColor: '#f7921a',
              boxShadow: '0 4px 6px rgba(159, 23, 62, 0.1)',
            }),
          }}
        />
      </div>
      
      <AlertModal
        show={showDeleteAlert}
        onHide={handleDeleteCancel}
        saveButtonText='Delete'
        cancelButtonText='Cancel'
        title="Delete Saved Search"
        message={`Are you sure you want to delete "${searchToDelete?.label || 'this saved search'}"?`}
        handleConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default SavedSearchFilter;
